import { useEffect, useRef, useState } from 'react';
import { collection, deleteDoc, doc, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ArrowClockwise, DownloadSimple, Trash } from 'phosphor-react';
import { db, functions } from '../../firebase';
import ExpandedNoticeModal from '../format-converter/components/ExpandedNoticeModal';
import NoticeCenter from '../format-converter/components/NoticeCenter';
import { formatDateTime, getTimestampMillis, truncateFileName } from '../format-converter/utils';

const isValidHttpUrl = (value) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (_error) {
    return false;
  }
};

const statusBadgeClass = (status) => {
  if (status === 'completed') return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
  if (status === 'failed') return 'bg-red-100 text-red-700 border border-red-200';
  if (status === 'processing') return 'bg-blue-100 text-blue-700 border border-blue-200';
  return 'bg-amber-100 text-amber-700 border border-amber-200';
};

const getNoticeFeedStorageKey = (uid) => `video-downloader:notice-feed:${uid || 'anonymous'}`;
const getActiveNoticeStorageKey = (uid) => `video-downloader:active-notice:${uid || 'anonymous'}`;

const readPersistedNoticeFeed = (uid) => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(getNoticeFeedStorageKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((entry) => entry && typeof entry.key === 'string' && typeof entry.message === 'string')
      .slice(0, 10);
  } catch (_error) {
    return [];
  }
};

const readPersistedActiveNoticeKey = (uid) => {
  if (typeof window === 'undefined') return '';
  try {
    return window.sessionStorage.getItem(getActiveNoticeStorageKey(uid)) || '';
  } catch (_error) {
    return '';
  }
};

function VideoDownloaderProject({ user: currentUser = null }) {
  const [sourceUrl, setSourceUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formInfo, setFormInfo] = useState('');
  const [jobs, setJobs] = useState([]);
  const [deletingJobId, setDeletingJobId] = useState('');
  const [retryingJobId, setRetryingJobId] = useState('');
  const [downloadingJobId, setDownloadingJobId] = useState('');
  const [noticeFeed, setNoticeFeed] = useState(() => readPersistedNoticeFeed(currentUser?.uid));
  const [activeNoticeKey, setActiveNoticeKey] = useState(() => readPersistedActiveNoticeKey(currentUser?.uid));
  const [isNoticeListOpen, setIsNoticeListOpen] = useState(false);
  const [expandedNotice, setExpandedNotice] = useState(null);
  const observedJobStateRef = useRef({});

  const showFloatingNotice = (message, tone = 'info', jobId = '', jobLabel = '') => {
    if (!message) return;

    const noticeKey = jobId ? `job:${jobId}` : `general:${tone}`;
    const title = jobLabel
      ? `Job: ${truncateFileName(jobLabel, 34)}`
      : jobId
        ? `Job ${jobId.slice(0, 8)}`
        : 'System';

    setNoticeFeed((prev) => {
      const now = Date.now();
      const next = [...prev];
      const index = next.findIndex((entry) => entry.key === noticeKey);
      const notice = {
        key: noticeKey,
        tone,
        title,
        message,
        updatedAt: now
      };
      if (index >= 0) {
        next[index] = notice;
      } else {
        next.push(notice);
      }
      next.sort((a, b) => b.updatedAt - a.updatedAt);
      return next.slice(0, 10);
    });

    setActiveNoticeKey(noticeKey);
  };

  useEffect(() => {
    if (!currentUser) {
      setJobs([]);
      return undefined;
    }

    const jobsQuery = query(
      collection(db, 'videoDownloadJobs'),
      where('uid', '==', currentUser.uid),
      orderBy('createdAt', 'desc'),
      limit(30)
    );

    const unsub = onSnapshot(
      jobsQuery,
      (snapshot) => {
        setJobs(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      },
      (error) => {
        setFormError(error.message || 'Could not load download jobs.');
      }
    );

    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setNoticeFeed([]);
      setActiveNoticeKey('');
      setIsNoticeListOpen(false);
      observedJobStateRef.current = {};
      return;
    }
    setNoticeFeed(readPersistedNoticeFeed(currentUser.uid));
    setActiveNoticeKey(readPersistedActiveNoticeKey(currentUser.uid));
    setIsNoticeListOpen(false);
    observedJobStateRef.current = {};
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!noticeFeed.length) {
      setActiveNoticeKey('');
      setIsNoticeListOpen(false);
      return;
    }
    if (!noticeFeed.some((entry) => entry.key === activeNoticeKey)) {
      setActiveNoticeKey(noticeFeed[0].key);
    }
  }, [noticeFeed, activeNoticeKey]);

  useEffect(() => {
    if (!currentUser?.uid || typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(getNoticeFeedStorageKey(currentUser.uid), JSON.stringify(noticeFeed));
    } catch (_error) {
      // Ignore sessionStorage write issues.
    }
  }, [noticeFeed, currentUser]);

  useEffect(() => {
    if (!currentUser?.uid || typeof window === 'undefined') return;
    try {
      if (activeNoticeKey) {
        window.sessionStorage.setItem(getActiveNoticeStorageKey(currentUser.uid), activeNoticeKey);
      } else {
        window.sessionStorage.removeItem(getActiveNoticeStorageKey(currentUser.uid));
      }
    } catch (_error) {
      // Ignore sessionStorage write issues.
    }
  }, [activeNoticeKey, currentUser]);

  useEffect(() => {
    const previousById = observedJobStateRef.current;
    const nextById = {};

    jobs.forEach((job) => {
      const status = job.status || 'queued';
      const error = job.error || '';
      const updatedAtMs = getTimestampMillis(job.updatedAt) || 0;
      const previous = previousById[job.id];
      const isFirstSeen = !previous;
      const hasStatusChanged = Boolean(previous) && previous.status !== status;
      const hasErrorChanged = Boolean(previous) && previous.error !== error && Boolean(error);

      const shouldNotifyFailure = status === 'failed' && (isFirstSeen || hasStatusChanged || hasErrorChanged);
      const shouldNotifyComplete = status === 'completed' && !isFirstSeen && hasStatusChanged;

      if (shouldNotifyFailure) {
        showFloatingNotice(`Failed: ${error || 'Download failed.'}`, 'error', job.id, job.title || job.url || '');
      } else if (shouldNotifyComplete) {
        showFloatingNotice('Update received: completed (100%).', 'success', job.id, job.title || job.url || '');
      }

      nextById[job.id] = { status, error, updatedAtMs };
    });

    observedJobStateRef.current = nextById;
  }, [jobs]);

  useEffect(() => {
    if (formError) showFloatingNotice(formError, 'error');
  }, [formError]);

  useEffect(() => {
    if (formInfo) showFloatingNotice(formInfo, 'info');
  }, [formInfo]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    setFormInfo('');

    const normalized = sourceUrl.trim();
    if (!normalized) {
      setFormError('Please paste a URL.');
      return;
    }
    if (!isValidHttpUrl(normalized)) {
      setFormError('Please enter a valid http(s) URL.');
      return;
    }

    setIsSubmitting(true);
    try {
      const startVideoDownload = httpsCallable(functions, 'startVideoDownload', {
        timeout: 60 * 60 * 1000
      });
      await startVideoDownload({ url: normalized });
      setFormInfo('Download job queued.');
      setSourceUrl('');
    } catch (error) {
      setFormError(error.message || 'Could not start download.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!jobId) return;
    setDeletingJobId(jobId);
    setFormError('');
    setFormInfo('');
    try {
      await deleteDoc(doc(db, 'videoDownloadJobs', jobId));
      setFormInfo('Download deleted.');
    } catch (error) {
      setFormError(error.message || 'Could not delete download job.');
    } finally {
      setDeletingJobId('');
    }
  };

  const handleRetryJob = async (job) => {
    if (!job?.id) return;
    setRetryingJobId(job.id);

    try {
      const retryVideoDownload = httpsCallable(functions, 'retryVideoDownload', {
        timeout: 60 * 60 * 1000
      });
      const response = await retryVideoDownload({ jobId: job.id, force: false });
      const result = response?.data || {};
      const message = result.message || (result.status === 'processing' ? 'Retry dispatched.' : 'Retry submitted.');
      showFloatingNotice(message, result.status === 'completed' ? 'success' : 'info', job.id, job.title || job.url || '');
    } catch (error) {
      const message = error.message || 'Could not retry download.';
      showFloatingNotice(message, 'error', job.id, job.title || job.url || '');
    } finally {
      setRetryingJobId('');
    }
  };

  const handleDownload = async (job) => {
    if (!job?.output?.downloadURL) return;
    setDownloadingJobId(job.id);
    try {
      const filename = job.output?.filename || `${job.id}.bin`;
      const downloadUrl = new URL(job.output.downloadURL);
      downloadUrl.searchParams.set('response-content-disposition', `attachment; filename="${filename}"`);
      downloadUrl.searchParams.set('response-content-type', 'application/octet-stream');

      const response = await fetch(downloadUrl.toString(), { method: 'GET', mode: 'cors' });
      if (!response.ok) {
        throw new Error(`Download failed (${response.status}).`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      showFloatingNotice(error.message || 'Could not download file.', 'error', job.id, job.title || job.url || '');
    } finally {
      setDownloadingJobId('');
    }
  };

  if (!currentUser) return null;
  const activeNotice = noticeFeed.find((entry) => entry.key === activeNoticeKey) || noticeFeed[0] || null;

  return (
    <main className="relative text-slate-900">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
        <section className="rounded-3xl border border-white/60 bg-white/85 p-6 shadow-xl shadow-brand-100/40 backdrop-blur">
          <div className="flex items-center gap-3">
            <img src="/apps/video-downloader/logo/logo.png" alt="Video Downloader logo" className="h-10 w-10 rounded-xl object-cover" />
            <h1 className="text-xl font-bold">Video Downloader</h1>
          </div>
          <p className="mt-1 text-sm text-slate-600">Paste a direct/public video page URL to download through the worker.</p>

          <form className="mt-4 flex flex-col gap-3 sm:flex-row" onSubmit={handleSubmit}>
            <input
              type="url"
              placeholder="https://example.com/video-page"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none ring-brand-500 transition focus:ring"
              value={sourceUrl}
              onChange={(event) => setSourceUrl(event.target.value)}
              required
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-xl bg-brand-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
            >
              {isSubmitting ? 'Queuing...' : 'Download'}
            </button>
          </form>
          {formError && <p className="mt-3 text-sm text-red-600">{formError}</p>}
        </section>

        <section className="rounded-3xl border border-white/60 bg-white/85 p-6 shadow-xl shadow-brand-100/40 backdrop-blur">
          <h2 className="text-lg font-bold">Recent Downloads</h2>
          <div className="mt-4 max-h-[500px] overflow-auto">
            <table className="w-full min-w-[760px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3">Created</th>
                  <th className="py-2 pr-3">Title</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Progress</th>
                  <th className="py-2 pr-3">Download</th>
                  <th className="py-2 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id} className="border-b border-slate-100">
                    <td className="py-2 pr-3 text-slate-600">{formatDateTime(job.createdAt)}</td>
                    <td className="py-2 pr-3" title={job.title || job.url || '-'}>
                      {truncateFileName(job.title || job.url || '-', 40)}
                    </td>
                    <td className="py-2 pr-3">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold capitalize ${statusBadgeClass(job.status)}`}>
                        {job.status || 'queued'}
                      </span>
                    </td>
                    <td className="py-2 pr-3">{job.progress ?? 0}%</td>
                    <td className="py-2 pr-3">
                      {job.output?.downloadURL ? (
                        <button
                          type="button"
                          onClick={() => handleDownload(job)}
                          disabled={downloadingJobId === job.id}
                          className="inline-flex items-center gap-1 font-semibold text-brand-700 underline decoration-brand-300 underline-offset-4 disabled:opacity-60"
                        >
                          <DownloadSimple size={14} weight="bold" />
                          {downloadingJobId === job.id ? 'Downloading...' : 'Download'}
                        </button>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleRetryJob(job)}
                          disabled={retryingJobId === job.id || job.status === 'processing'}
                          className="inline-flex items-center justify-center rounded-md bg-amber-100 p-2 text-amber-800 transition hover:bg-amber-200 disabled:cursor-not-allowed disabled:opacity-60"
                          aria-label={retryingJobId === job.id ? 'Retrying job' : 'Retry job'}
                          title={retryingJobId === job.id ? 'Retrying' : 'Retry'}
                        >
                          <ArrowClockwise size={14} weight="bold" className={retryingJobId === job.id ? 'animate-spin' : ''} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteJob(job.id)}
                          disabled={deletingJobId === job.id}
                          className="inline-flex items-center justify-center rounded-md bg-slate-100 p-2 text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
                          aria-label={deletingJobId === job.id ? 'Deleting job' : 'Delete job'}
                          title={deletingJobId === job.id ? 'Deleting' : 'Delete'}
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!jobs.length && (
                  <tr>
                    <td className="py-4 text-slate-500" colSpan={6}>
                      No download jobs yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <NoticeCenter
        activeNotice={activeNotice}
        noticeFeed={noticeFeed}
        isNoticeListOpen={isNoticeListOpen}
        setIsNoticeListOpen={setIsNoticeListOpen}
        setActiveNoticeKey={setActiveNoticeKey}
        setExpandedNotice={setExpandedNotice}
      />
      <ExpandedNoticeModal expandedNotice={expandedNotice} onClose={() => setExpandedNotice(null)} />
    </main>
  );
}

export default VideoDownloaderProject;
